import styled from '@emotion/styled';
import { useField } from 'formik';
import { maxBy } from 'lodash';
import React, { useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { FaPlus } from 'react-icons/fa';
import { generatePath, Link, useParams } from 'react-router-dom';

import { assessmentManagerChildQuestionsRoute } from 'App/common/routeLookup';
import InvalidMessage from 'App/common/styles/InvalidMessage';
import DeleteModal from 'App/components/DeleteModal';
import useIsPermitted from 'App/utils/hooks/useIsPermitted';
import { PERMISSION_TYPES } from 'App/utils/isPermitted/isPermitted';
import { NumberField } from 'Shared/form/NumberField';
import { TextField } from 'Shared/form/TextField';
import questionPropType from '../types/questionPropType';
import createQuestionId from '../utils/createQuestionId';
import { getDuplicateAnswerError } from '../utils/getDuplicateAnswerError';
import QuestionAccordionWrapper from './QuestionAccordionWrapper';

const AnswerRowStyle = styled(Row)`
    margin-top: 0.5em;

    & .btn-link {
        padding: 6px 4px;
    }
`;

const QuestionAnswersSection = ({ question }) => {
    const isClinicalAdmin = useIsPermitted(PERMISSION_TYPES.CLINICAL_ADMIN);
    const canEditAnswers = isClinicalAdmin || question.can_delete;
    const [{ value: answers }, { error }, { setValue }] = useField({
        name: `questions.${question.question_id}.answers`,
        validate: (value) => {
            if (Object.keys(value).length < 1) {
                return 'Question must have answer options';
            }
            if (question.type === 'checkbox') {
                return getDuplicateAnswerError(value);
            }
        },
    });
    const [{ value: isQuestionMobileEnabled }] = useField(`questions.${question.question_id}.attributes.is_mobile`);
    const shouldShowMobileAnswers = isClinicalAdmin || isQuestionMobileEnabled;
    const [{ value: isQuestionScoringEnabled }] = useField(
        `questions.${question.question_id}.attributes.scoring_enabled`
    );
    const { categoryId, assessmentId } = useParams();
    const [answerToDelete, setAnswerToDelete] = useState(null);

    const addNewAnswer = () => {
        const newId = createQuestionId();
        const newPosition = (Number(maxBy(Object.values(answers), (a) => Number(a.position))?.position) || 0) + 1;
        setValue({
            ...answers,
            [newId]: {
                new: true, // required for STM API
                position: newPosition,
            },
        });
    };

    const deleteAnswer = (answerId) => {
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line react-hooks/immutability
        delete answers[answerId];
        setValue(answers);
    };

    return (
        <QuestionAccordionWrapper
            questionId={question.question_id}
            statePath="sections.answers"
            title="Answers"
            headerClassName="assessment-manager-question-section-header"
        >
            <div>
                {canEditAnswers && (
                    <Button variant="outline-secondary" onClick={addNewAnswer}>
                        <FaPlus /> Add Answer
                    </Button>
                )}

                {typeof error === 'string' && <InvalidMessage>{error}</InvalidMessage>}

                {Object.keys(answers).length > 0 && (
                    <Row>
                        <Col md={2}>
                            <legend className="field-required">Position</legend>
                        </Col>
                        <Col md={3}>
                            <legend className="field-required">Value</legend>
                        </Col>
                        {shouldShowMobileAnswers && (
                            <Col md={2}>
                                <legend className={isQuestionMobileEnabled && 'field-required'}>Mobile Value</legend>
                            </Col>
                        )}
                        {isQuestionScoringEnabled && (
                            <Col md={2}>
                                <legend className="field-required">Scoring Multiplier</legend>
                            </Col>
                        )}
                    </Row>
                )}

                {Object.keys(answers).map((answerId) => (
                    <AnswerRowStyle key={answerId}>
                        <Col md={2}>
                            <NumberField
                                fieldName={`questions.${question.question_id}.answers.${answerId}.position`}
                                placeholder="#"
                                aria-label="Position"
                                disabled={!canEditAnswers}
                                required
                                debounce
                            />
                        </Col>
                        <Col md={3}>
                            <TextField
                                fieldName={`questions.${question.question_id}.answers.${answerId}.option`}
                                placeholder="Answer Value"
                                aria-label="Answer Value"
                                disabled={!canEditAnswers}
                                required
                                debounce
                            />
                        </Col>
                        {shouldShowMobileAnswers && (
                            <Col md={2}>
                                <TextField
                                    fieldName={`questions.${question.question_id}.answers.${answerId}.mobile_option`}
                                    placeholder="Mobile Value"
                                    aria-label="Mobile Value"
                                    disabled={!canEditAnswers}
                                    required={isQuestionMobileEnabled}
                                    debounce
                                />
                            </Col>
                        )}
                        {isQuestionScoringEnabled && (
                            <Col md={2}>
                                <NumberField
                                    fieldName={`questions.${question.question_id}.attributes.answerWeights.${answerId}`}
                                    placeholder="#"
                                    aria-label="Scoring Multiplier"
                                    required
                                    debounce
                                />
                            </Col>
                        )}
                        <Col md={3}>
                            <Link
                                to={generatePath(assessmentManagerChildQuestionsRoute, {
                                    categoryId,
                                    assessmentId,
                                    parentQuestionId: question.question_id,
                                    parentAnswerId: answerId,
                                })}
                            >
                                <Button variant="link">
                                    {question.children?.[answerId] ? 'Has Child Questions' : 'Add Child Questions'}
                                </Button>
                            </Link>
                            {canEditAnswers && (
                                <Button
                                    variant="link"
                                    onClick={() => {
                                        if (!answers[answerId].new) {
                                            setAnswerToDelete(answerId);
                                        } else {
                                            deleteAnswer(answerId);
                                        }
                                    }}
                                >
                                    Delete
                                </Button>
                            )}
                        </Col>
                    </AnswerRowStyle>
                ))}
                {answerToDelete && (
                    <DeleteModal
                        show
                        title="Delete Answer"
                        message={
                            <div>
                                <p>Are you sure you want to delete this answer?</p>
                                <p>User responses will be unlinked and sub-questions will also be deleted.</p>
                            </div>
                        }
                        onHide={() => setAnswerToDelete(null)}
                        onDelete={() => {
                            deleteAnswer(answerToDelete);
                            setAnswerToDelete(null);
                        }}
                    />
                )}
            </div>
        </QuestionAccordionWrapper>
    );
};

QuestionAnswersSection.propTypes = {
    question: questionPropType.isRequired,
};

export default QuestionAnswersSection;
